import type { QueryClient } from "@tanstack/react-query";
import type { WipQueryParams } from "../api/operationsScreensApi";
import { QK, summitOpsApi } from "../api/summitOpsApi";
import { clearDataPrefetchers, registerDataPrefetch } from "./preload";

// Release 1.5.0 (perf, behind the 1.5.0 switch) — hovering or focusing a link to one of the heavy screens also starts
// its main request, with exactly the query key and request the screen itself uses (so the screen finds it in the cache).
// The heavier API modules are imported on demand, so this adds almost nothing to the first download. An answer younger
// than a minute is not asked for again.

const FRESH_MS = 60_000;

export function installDataPrefetch(queryClient: QueryClient): () => void {
  registerDataPrefetch("/operations", async () => {
    await queryClient.prefetchQuery({ queryKey: [...QK.operationsHub], queryFn: summitOpsApi.getOperationsHub, staleTime: FRESH_MS });
  });
  registerDataPrefetch("/shop-floor/dispatch", async () => {
    const { FLOOR_QK, floorApi } = await import("../api/floorApi");
    await queryClient.prefetchQuery({
      queryKey: [...FLOOR_QK.dispatchByRule("CriticalRatio")],
      queryFn: () => floorApi.getDispatchBoardByRule({ rule: "CriticalRatio" }),
      staleTime: FRESH_MS
    });
  });
  registerDataPrefetch("/operations/wip", async () => {
    const { OPS_SCREENS_QK, operationsScreensApi } = await import("../api/operationsScreensApi");
    // The WIP screen's own defaults (WipScreen.tsx) — the same key, so the screen finds the answer.
    const filters: WipQueryParams = { part: "", partMode: "contains", customer: "", shop: "", workcenter: "", stage: "", bucket: "" };
    await queryClient.prefetchQuery({
      queryKey: OPS_SCREENS_QK.wip(filters),
      queryFn: () => operationsScreensApi.getWip(filters),
      staleTime: FRESH_MS
    });
  });
  registerDataPrefetch("/shop-floor/material", async () => {
    // The launch board is the first table the screen draws (its other two lists depend on the screen's filters).
    const { SHOPFLOOR_QK, shopFloorApi } = await import("../api/shopFloorApi");
    await queryClient.prefetchQuery({ queryKey: [...SHOPFLOOR_QK.launchBoard()], queryFn: () => shopFloorApi.getLaunchBoard(), staleTime: FRESH_MS });
  });
  registerDataPrefetch("/shop-floor/osp", async () => {
    const { FLOW_QK, flowApi } = await import("../api/flowApi");
    await queryClient.prefetchQuery({ queryKey: FLOW_QK.ospBoard(), queryFn: () => flowApi.getOspBoard(), staleTime: FRESH_MS });
  });
  return clearDataPrefetchers;
}
